import React, { useState, useEffect, useCallback } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarIcon, Clock, MapPin, User, Car, CheckCircle, ArrowRight, Pencil, DollarSign, Plus, Truck, Route, Repeat, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { formatDate } from '../lib/utils';
import { PasajeroItem } from '../components/PasajeroItem';
import { parsearPasajerosDeServicio, serializarPasajeros, pasajeroVacio, resumirRutaServicio } from '../lib/pasajerosUtils';

moment.locale('es');
const localizer = momentLocalizer(moment);

const estadoBadgeColors = {
  'Agendado': 'bg-blue-100 text-blue-800',
  'Servicio Recibido': 'bg-amber-100 text-amber-800',
  'Realizado': 'bg-green-100 text-green-800',
  'Suspendido': 'bg-red-100 text-red-800'
};

// Colores para el calendario según estado y confirmación
const getEventColor = (estado, confirmado) => {
  if (estado === 'Realizado') return '#10B981'; // Verde - Completado
  if (estado === 'Suspendido') return '#EF4444'; // Rojo - Suspendido
  if (estado === 'Servicio Recibido') return '#F59E0B'; // Ámbar - Chofer va al servicio
  if (estado === 'Agendado' && confirmado) return '#6366F1'; // Índigo - Confirmado
  return '#3B82F6'; // Azul - Pendiente (Agendado sin confirmar)
};

// Leyenda de colores para el calendario
const CalendarLegend = () => (
  <div className="flex flex-wrap items-center gap-4 mt-4 p-3 bg-gray-50 rounded-lg border">
    <span className="text-sm font-medium text-gray-600">Leyenda:</span>
    <div className="flex items-center gap-1.5">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#3B82F6' }}></div>
      <span className="text-sm text-gray-700">Pendiente</span>
    </div>
    <div className="flex items-center gap-1.5">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#6366F1' }}></div>
      <span className="text-sm text-gray-700">Confirmado</span>
    </div>
    <div className="flex items-center gap-1.5">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#F59E0B' }}></div>
      <span className="text-sm text-gray-700">Servicio Recibido</span>
    </div>
    <div className="flex items-center gap-1.5">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10B981' }}></div>
      <span className="text-sm text-gray-700">Realizado</span>
    </div>
    <div className="flex items-center gap-1.5">
      <div className="w-4 h-4 rounded" style={{ backgroundColor: '#EF4444' }}></div>
      <span className="text-sm text-gray-700">Suspendido</span>
    </div>
  </div>
);

export const CalendarioPage = () => {
  const { user } = useAuth();
  const [servicios, setServicios] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [selectedServicio, setSelectedServicio] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showTarifaModal, setShowTarifaModal] = useState(false);
  const [tarifaConfirmar, setTarifaConfirmar] = useState('');
  const [showRealizadoModal, setShowRealizadoModal] = useState(false);
  const [tarifaRealizado, setTarifaRealizado] = useState('');
  const [tiempoEspera, setTiempoEspera] = useState('0');
  const [showTiempoEsperaModal, setShowTiempoEsperaModal] = useState(false);
  const [tiempoEsperaEdit, setTiempoEsperaEdit] = useState('0');
  const [vehiculos, setVehiculos] = useState([]);
  const [conductores, setConductores] = useState([]);
  const [selectedVehiculo, setSelectedVehiculo] = useState('');
  const [selectedConductor, setSelectedConductor] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [loading, setLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    fecha: '',
    hora: '',
    pasajeros: '',
    tarifa: '',
    centro_costo: '',
    observaciones: '',
    tipo_viaje: ''
  });
  // Lista estructurada de pasajeros para edición multi-pasajero
  const [editPasajeros, setEditPasajeros] = useState([pasajeroVacio()]);
  const [editDesvios, setEditDesvios] = useState([]);
  const [editTieneRetorno, setEditTieneRetorno] = useState(false);
  const [centrosCostoEdit, setCentrosCostoEdit] = useState([]);
  const [tiposViaje, setTiposViaje] = useState([]);
  // Reasignación de conductor
  const [showReasignar, setShowReasignar] = useState(false);
  const [reasignarConductor, setReasignarConductor] = useState('');
  const [reasignarMotivo, setReasignarMotivo] = useState('');
  const [reasignarLoading, setReasignarLoading] = useState(false);

  useEffect(() => {
    loadServicios();
    if (user?.role === 'admin') {
      loadVehiculos();
      loadConductores();
    }
    loadTiposViaje();
  }, [user]);

  const loadServicios = async () => {
    try {
      const response = await api.get('/servicios');
      setServicios(response.data);
      
      const eventosFormatted = response.data.map(s => {
        const dateTime = new Date(`${s.fecha}T${s.hora}`);
        const { origen: origenResumen, destino: destinoResumen } = resumirRutaServicio(s);
        return {
          id: s.id,
          title: `${s.hora} ${origenResumen} → ${destinoResumen}`,
          start: dateTime,
          end: dateTime,
          resource: { ...s, origen_resumen: origenResumen, destino_resumen: destinoResumen }
        };
      });
      setEventos(eventosFormatted);
    } catch (error) {
      toast.error('Error al cargar servicios');
    }
  };

  const loadVehiculos = async () => {
    try {
      const response = await api.get('/vehiculos');
      setVehiculos(response.data);
    } catch (error) {
      console.error('Error al cargar vehículos');
    }
  };

  const loadConductores = async () => {
    try {
      const response = await api.get('/conductores');
      setConductores(response.data);
    } catch (error) {
      console.error('Error al cargar conductores');
    }
  };

  const loadTiposViaje = async () => {
    try {
      const response = await api.get('/tipos-viaje');
      setTiposViaje(response.data || []);
    } catch (error) {
      console.error('Error al cargar tipos de viaje');
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedServicio(event.resource);
    setShowDetail(true);
  };

  const handleAssign = () => {
    if (selectedServicio?.vehiculo_id) {
      setSelectedVehiculo(selectedServicio.vehiculo_id);
    }
    if (selectedServicio?.conductor_id) {
      setSelectedConductor(selectedServicio.conductor_id);
    }
    setShowDetail(false);
    setShowAssign(true);
  };

  const handleOpenReasignar = () => {
    setReasignarConductor('');
    setReasignarMotivo('');
    setShowDetail(false);
    setShowReasignar(true);
  };

  const handleReasignarSubmit = async () => {
    if (!reasignarConductor) {
      toast.error('Selecciona el nuevo conductor');
      return;
    }
    setReasignarLoading(true);
    try {
      const res = await api.post(`/servicios/${selectedServicio.id}/reasignar-conductor`, {
        conductor_id: reasignarConductor,
        motivo: reasignarMotivo.trim() || null,
      });
      toast.success(`Reasignado a ${res.data.conductor_nuevo}`);
      setShowReasignar(false);
      await loadServicios();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reasignar');
    } finally {
      setReasignarLoading(false);
    }
  };

  const handleSaveAssignment = async () => {
    setLoading(true);
    try {
      await api.put(`/servicios/${selectedServicio.id}`, {
        vehiculo_id: selectedVehiculo || null,
        conductor_id: selectedConductor || null
      });
      toast.success('Asignación guardada');
      await loadServicios();
      setShowAssign(false);
      setSelectedVehiculo('');
      setSelectedConductor('');
    } catch (error) {
      toast.error('Error al guardar asignación');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (selectedServicio) {
      // Cargar centros de costo del cliente del servicio
      if (selectedServicio.cliente_id) {
        try {
          const response = await api.get(`/clientes/${selectedServicio.cliente_id}/centros-costo`);
          setCentrosCostoEdit(response.data.centros_costo || []);
        } catch (error) {
          setCentrosCostoEdit([]);
        }
      } else {
        setCentrosCostoEdit([]);
      }
      
      // Parsear pasajeros desde el servicio (soporta 1 o N pasajeros)
      const { pasajeros: pasajerosParseados, observacionesUsuario } = parsearPasajerosDeServicio(selectedServicio);
      setEditPasajeros(pasajerosParseados.length > 0 ? pasajerosParseados : [pasajeroVacio()]);

      setEditForm({
        fecha: selectedServicio.fecha || '',
        hora: selectedServicio.hora || '',
        pasajeros: selectedServicio.pasajeros?.toString() || '',
        tarifa: selectedServicio.tarifa?.toString() || '',
        centro_costo: selectedServicio.centro_costo || '',
        observaciones: observacionesUsuario || '',
        tipo_viaje: selectedServicio.tipo_viaje || '',
        conductor_id: selectedServicio.conductor_id || '',
        vehiculo_id: selectedServicio.vehiculo_id || ''
      });
      setEditDesvios(Array.isArray(selectedServicio.desvios) ? selectedServicio.desvios : []);
      setEditTieneRetorno(!!selectedServicio.tiene_retorno);
      setShowDetail(false);
      setShowEdit(true);
    }
  };

  const handleSaveEdit = async () => {
    // Validar pasajeros: nombre, origen y destino son obligatorios
    const pasajerosValidos = editPasajeros.every(p => p.nombre?.trim() && p.origen?.trim() && p.destino?.trim());
    if (!pasajerosValidos) {
      toast.error('Complete nombre, origen y destino de cada pasajero');
      return;
    }

    setLoading(true);
    try {
      // Serializar pasajeros al formato del backend
      const serializado = serializarPasajeros(editPasajeros, editForm.observaciones);

      await api.put(`/servicios/${selectedServicio.id}`, {
        fecha: editForm.fecha,
        hora: editForm.hora,
        origen: serializado.origen,
        destino: serializado.destino,
        pasajeros: serializado.pasajeros,
        telefono: serializado.telefono || null,
        correo: serializado.correo || null,
        tarifa: parseFloat(editForm.tarifa) || 0,
        centro_costo: editForm.centro_costo || null,
        observaciones: serializado.observaciones || null,
        nombre_pasajero: serializado.nombre_pasajero || null,
        tipo_viaje: editForm.tipo_viaje || null,
        desvios: (editDesvios || []).map(d => (d || '').trim()).filter(Boolean),
        tiene_retorno: !!editTieneRetorno,
        conductor_id: editForm.conductor_id || null,
        vehiculo_id: editForm.vehiculo_id || null
      });
      toast.success('Servicio actualizado correctamente');
      await loadServicios();
      setShowEdit(false);
    } catch (error) {
      console.error('Error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Error al actualizar el servicio');
    } finally {
      setLoading(false);
    }
  };

  // Helpers para manipular pasajeros en el modal de edición
  const actualizarPasajeroEdit = (index, data) => {
    const nuevos = [...editPasajeros];
    nuevos[index] = data;
    setEditPasajeros(nuevos);
  };
  const agregarPasajeroEdit = () => {
    setEditPasajeros([...editPasajeros, pasajeroVacio()]);
  };
  const eliminarPasajeroEdit = (index) => {
    if (editPasajeros.length > 1) {
      setEditPasajeros(editPasajeros.filter((_, i) => i !== index));
    }
  };
  const moverPasajeroEditArriba = (index) => {
    if (index > 0) {
      const nuevos = [...editPasajeros];
      [nuevos[index - 1], nuevos[index]] = [nuevos[index], nuevos[index - 1]];
      setEditPasajeros(nuevos);
    }
  };
  const moverPasajeroEditAbajo = (index) => {
    if (index < editPasajeros.length - 1) {
      const nuevos = [...editPasajeros];
      [nuevos[index], nuevos[index + 1]] = [nuevos[index + 1], nuevos[index]];
      setEditPasajeros(nuevos);
    }
  };

  const handleChangeEstado = async (nuevoEstado) => {
    // Si el nuevo estado es "Realizado", mostrar modal de confirmación con opción de editar tarifa
    if (nuevoEstado === 'Realizado') {
      setTarifaRealizado(selectedServicio.tarifa?.toString() || '0');
      setTiempoEspera('0');
      setShowRealizadoModal(true);
      return;
    }
    
    setLoading(true);
    try {
      await api.put(`/servicios/${selectedServicio.id}`, {
        estado: nuevoEstado
      });
      toast.success(`Estado cambiado a ${nuevoEstado}`);
      await loadServicios();
      setShowDetail(false);
    } catch (error) {
      toast.error('Error al cambiar estado');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarRealizado = async () => {
    const nuevaTarifa = parseFloat(tarifaRealizado);
    if (isNaN(nuevaTarifa) || nuevaTarifa < 0) {
      toast.error('La tarifa debe ser un número válido');
      return;
    }

    const te = parseInt(tiempoEspera) || 0;

    setLoading(true);
    try {
      await api.put(`/servicios/${selectedServicio.id}`, {
        tarifa: nuevaTarifa,
        estado: 'Realizado',
        tiempo_espera: te
      });
      toast.success('Servicio marcado como Realizado');
      await loadServicios();
      setShowDetail(false);
      setShowRealizadoModal(false);
    } catch (error) {
      toast.error('Error al actualizar el servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async () => {
    // Para admin, verificar si necesita ingresar tarifa
    if (user?.role === 'admin' && (!selectedServicio.tarifa || selectedServicio.tarifa === 0)) {
      setTarifaConfirmar('');
      setShowTarifaModal(true);
      return;
    }
    
    await confirmarServicio();
  };

  const confirmarServicio = async () => {
    setLoading(true);
    try {
      await api.put(`/servicios/${selectedServicio.id}/confirmar`, { confirmado: true });
      toast.success('Servicio confirmado');
      await loadServicios();
      setShowDetail(false);
      setShowTarifaModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTiempoEspera = () => {
    setTiempoEsperaEdit(selectedServicio.tiempo_espera?.toString() || '0');
    setShowTiempoEsperaModal(true);
  };

  const handleGuardarTiempoEspera = async () => {
    const te = parseInt(tiempoEsperaEdit) || 0;
    setLoading(true);
    try {
      await api.put(`/servicios/${selectedServicio.id}`, { tiempo_espera: te });
      toast.success('Tiempo de espera actualizado');
      await loadServicios();
      setShowTiempoEsperaModal(false);
      setShowDetail(false);
    } catch (error) {
      toast.error('Error al actualizar tiempo de espera');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarConTarifa = async () => {
    if (!tarifaConfirmar || parseFloat(tarifaConfirmar) <= 0) {
      toast.error('Debe ingresar una tarifa válida');
      return;
    }

    setLoading(true);
    try {
      // Primero actualizar la tarifa
      await api.put(`/servicios/${selectedServicio.id}`, {
        ...selectedServicio,
        tarifa: parseFloat(tarifaConfirmar)
      });
      
      // Luego confirmar el servicio
      await api.put(`/servicios/${selectedServicio.id}/confirmar`, { confirmado: true });
      
      toast.success('Tarifa guardada y servicio confirmado');
      await loadServicios();
      setShowDetail(false);
      setShowTarifaModal(false);
      setTarifaConfirmar('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await api.delete(`/servicios/${selectedServicio.id}`);
      toast.success('Servicio eliminado');
      await loadServicios();
      setShowDelete(false);
      setShowDetail(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const handleViewChange = useCallback((newView) => {
    setView(newView);
  }, []);

  const eventStyleGetter = (event) => {
    const estado = event.resource?.estado || 'Agendado';
    const confirmado = event.resource?.confirmado || false;
    const baseStyle = {
      backgroundColor: getEventColor(estado, confirmado),
      borderRadius: '4px',
      opacity: 0.9,
      color: 'white',
      border: '0px',
      display: 'flex',
      alignItems: 'center',
      padding: '1px 4px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      height: '100%'
    };

    // Vista mes: tamaño más pequeño
    if (view === 'month') {
      return {
        style: {
          ...baseStyle,
          fontSize: '0.65rem',
          lineHeight: '1.1'
        }
      };
    }

    // Vistas semana y día: fuente pequeña para que quepa
    return {
      style: {
        ...baseStyle,
        fontSize: '0.7rem',
        lineHeight: '1.2'
      }
    };
  };

  // Componente para vista Mes
  const MonthEvent = ({ event }) => (
    <span style={{ 
      display: 'block',
      width: '100%',
      fontSize: '0.65rem', 
      lineHeight: '1.1',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }}>
      {event.resource.hora} {event.resource.origen_resumen || event.resource.origen} → {event.resource.destino_resumen || event.resource.destino}
    </span>
  );

  // Componente para vista Semana y Día (SIN hora porque BigCalendar ya la muestra)
  const WeekDayEvent = ({ event }) => (
    <span style={{ 
      display: 'block',
      width: '100%',
      fontSize: '0.7rem', 
      lineHeight: '1.2',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }}>
      {event.resource.origen_resumen || event.resource.origen} → {event.resource.destino_resumen || event.resource.destino}
    </span>
  );

  // Componente para vista Lista (Agenda) - Solo muestra origen/destino
  // La fecha y hora ya las muestra react-big-calendar en sus columnas
  // Pero para evitar que falte la fecha cuando hay múltiples eventos el mismo día,
  // incluimos la fecha en el evento
  const AgendaEvent = ({ event }) => {
    const fecha = moment(event.start).format('DD-MM-YYYY');
    return (
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        color: '#1e293b',
        fontSize: '0.875rem',
      }}>
        <span style={{ 
          fontWeight: '600',
          minWidth: '90px',
          color: '#374151'
        }}>
          {fecha}
        </span>
        <span style={{ 
          fontWeight: '500',
          minWidth: '50px',
          color: '#64748b'
        }}>
          {event.resource.hora}
        </span>
        <span style={{ fontWeight: '500' }}>
          {event.resource.origen_resumen || event.resource.origen} → {event.resource.destino_resumen || event.resource.destino}
        </span>
      </div>
    );
  };

  // Componente personalizado para la fecha en la agenda (siempre muestra la fecha)
  const AgendaDateComponent = ({ day, label }) => {
    return (
      <span style={{ 
        fontWeight: '600',
        color: '#1e293b',
        fontSize: '0.875rem'
      }}>
        {moment(day).format('DD-MM-YYYY')}
      </span>
    );
  };

  // Componente personalizado para la hora en la agenda
  const AgendaTimeComponent = ({ event }) => {
    return (
      <span style={{ 
        fontWeight: '500',
        color: '#64748b',
        fontSize: '0.875rem'
      }}>
        {event.resource.hora}
      </span>
    );
  };

  const canDelete = () => {
    if (!selectedServicio) return false;
    if (user?.role === 'admin') return true;
    if (user?.role === 'client' && selectedServicio.estado === 'Agendado' && !isServicioPasado()) return true;
    return false;
  };

  const canSuspend = () => {
    if (!selectedServicio) return false;
    if (selectedServicio.estado !== 'Agendado') return false;
    if (user?.role === 'admin') return true;
    if (user?.role === 'client' && !isServicioPasado()) return true;
    return false;
  };

  const isServicioPasado = () => {
    if (!selectedServicio) return true;
    const ahora = new Date();
    const fechaServicio = new Date(`${selectedServicio.fecha}T${selectedServicio.hora}`);
    return fechaServicio < ahora;
  };

  // Admin puede confirmar un servicio agendado que aún no está confirmado
  const canConfirmAdmin = () => {
    if (user?.role !== 'admin') return false;
    return selectedServicio?.estado === 'Agendado' && !selectedServicio?.confirmado;
  };

  // Admin puede marcar realizado solo si el servicio ya está confirmado
  const canMarkRealizado = () => {
    if (user?.role !== 'admin') return false;
    return selectedServicio?.estado === 'Agendado' && selectedServicio?.confirmado;
  };

  return (
    <div className="space-y-6" data-testid="calendario-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Calendario Operativo</h1>
          <p className="text-gray-600 mt-1">Gestión de servicios de traslado</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <div style={{ height: '700px' }}>
            <BigCalendar
              localizer={localizer}
              events={eventos}
              startAccessor="start"
              endAccessor="end"
              date={currentDate}
              onNavigate={handleNavigate}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventStyleGetter}
              view={view}
              onView={handleViewChange}
              views={['month', 'week', 'day', 'agenda']}
              components={{
                month: {
                  event: MonthEvent
                },
                week: {
                  event: WeekDayEvent
                },
                day: {
                  event: WeekDayEvent
                },
                agenda: {
                  event: AgendaEvent,
                  date: AgendaDateComponent,
                  time: AgendaTimeComponent
                }
              }}
              messages={{
                next: 'Siguiente',
                previous: 'Anterior',
                today: 'Hoy',
                month: 'Mes',
                week: 'Semana',
                day: 'Día',
                agenda: 'Lista',
                date: 'Fecha',
                time: 'Hora',
                event: 'Servicio',
                noEventsInRange: 'No hay servicios en este rango',
                showMore: (total) => `+ Ver más (${total})`
              }}
              formats={{
                timeGutterFormat: 'HH:mm',
                eventTimeRangeFormat: () => null,
                agendaTimeRangeFormat: ({ start }) => moment(start).format('HH:mm'),
                agendaDateFormat: 'DD-MM-YYYY',
                dayHeaderFormat: 'dddd DD-MM-YYYY',
                dayRangeHeaderFormat: ({ start, end }) => 
                  `${moment(start).format('DD-MM-YYYY')} - ${moment(end).format('DD-MM-YYYY')}`,
              }}
            />
          </div>
          <CalendarLegend />
        </CardContent>
      </Card>

      {/* Detalle del Servicio */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="servicio-detail-modal">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold font-['Manrope']">Detalle del Servicio</DialogTitle>
              {selectedServicio?.folio && (
                <div className="bg-primary/10 text-primary px-4 py-2 rounded-lg">
                  <span className="text-sm font-medium">Folio</span>
                  <span className="text-2xl font-bold ml-2">#{selectedServicio.folio}</span>
                </div>
              )}
            </div>
          </DialogHeader>

          {selectedServicio && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Badge className={estadoBadgeColors[selectedServicio.estado]}>
                  {selectedServicio.estado}
                </Badge>
                {selectedServicio.confirmado && (
                  <Badge className="bg-green-600 text-white">
                    <CheckCircle size={14} className="mr-1" />
                    Confirmado
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Fecha</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <CalendarIcon size={16} className="text-primary" />
                    <span className="font-medium">{formatDate(selectedServicio.fecha)}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-600">Hora</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock size={16} className="text-primary" />
                    <span className="font-medium">{selectedServicio.hora}</span>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-gray-600">Cliente</Label>
                <p className="font-medium mt-1">{selectedServicio.cliente_nombre || selectedServicio.empresa_nombre}</p>
              </div>

              <div>
                <Label className="text-gray-600">Usuario Solicitante</Label>
                <p className="font-medium mt-1">{selectedServicio.usuario_solicitante_nombre}</p>
              </div>

              {selectedServicio.pasajeros && (
                <div>
                  <Label className="text-gray-600">Pasajeros</Label>
                  <p className="font-medium mt-1">
                    {selectedServicio.pasajeros.split(',').map(p => p.trim()).join(' - ')}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Origen</Label>
                  <div className="flex items-start gap-2 mt-1">
                    <MapPin size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="font-medium whitespace-pre-line">{resumirRutaServicio(selectedServicio).origen}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-600">Destino</Label>
                  <div className="flex items-start gap-2 mt-1">
                    <MapPin size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="font-medium whitespace-pre-line">{resumirRutaServicio(selectedServicio).destino}</span>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-gray-600">Tarifa</Label>
                {user?.role === 'client' && (!selectedServicio.tarifa || selectedServicio.tarifa === 0) ? (
                  <p className="font-medium text-amber-600 mt-1">POR CONFIRMAR</p>
                ) : (
                  <p className="font-medium text-primary mt-1">${selectedServicio.tarifa?.toLocaleString('es-CL')}</p>
                )}
              </div>

              {selectedServicio.centro_costo && (
                <div>
                  <Label className="text-gray-600">Centro de Costo</Label>
                  <p className="font-medium mt-1">{selectedServicio.centro_costo}</p>
                </div>
              )}

              {selectedServicio.tipo_viaje && (
                <div>
                  <Label className="text-gray-600">Tipo de Viaje</Label>
                  <p className="font-medium mt-1">{selectedServicio.tipo_viaje}</p>
                </div>
              )}

              {/* Observaciones sin el detalle de pasajeros */}
              {selectedServicio.observaciones && !selectedServicio.observaciones.includes('--- Detalle de Ruta') && (
                <div>
                  <Label className="text-gray-600">Observaciones</Label>
                  <p className="font-medium mt-1 text-gray-700 whitespace-pre-line">
                    {selectedServicio.observaciones}
                  </p>
                </div>
              )}
              
              {selectedServicio.observaciones && selectedServicio.observaciones.includes('--- Detalle de Ruta') && (
                <div>
                  <Label className="text-gray-600">Observaciones</Label>
                  <p className="font-medium mt-1 text-gray-700 whitespace-pre-line">
                    {selectedServicio.observaciones.split('--- Detalle de Ruta')[0].trim()}
                  </p>
                </div>
              )}

              {/* Ruta del Vehículo: desvíos y retorno */}
              {((selectedServicio.desvios && selectedServicio.desvios.length > 0) || selectedServicio.tiene_retorno) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="detalle-ruta-vehiculo">
                  <Label className="text-gray-700 font-semibold mb-2 flex items-center gap-2">
                    <Route size={16} className="text-amber-700" />
                    Ruta del Vehículo
                  </Label>
                  <ol className="text-sm space-y-1 ml-1">
                    <li className="flex items-start gap-2"><span className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />Origen: <span className="font-medium whitespace-pre-line">{resumirRutaServicio(selectedServicio).origen}</span></li>
                    {(selectedServicio.desvios || []).map((d, i) => (
                      <li key={i} className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500" />Desvío {i + 1}: <span className="font-medium">{d}</span></li>
                    ))}
                    <li className="flex items-start gap-2"><span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />Destino: <span className="font-medium whitespace-pre-line">{resumirRutaServicio(selectedServicio).destino}</span></li>
                    {selectedServicio.tiene_retorno && (
                      <li className="flex items-center gap-2 mt-1 text-amber-700 font-medium">
                        <Repeat size={14} /> Retorno al origen
                      </li>
                    )}
                  </ol>
                </div>
              )}

              {/* Tabla de pasajeros - siempre visible */}
              <div className="mt-4">
                <Label className="text-gray-600 mb-2 block">Detalle de Pasajeros</Label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Nombre</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Ruta</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Contacto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(() => {
                        // Verificar si hay múltiples pasajeros en observaciones
                        const obs = selectedServicio.observaciones || '';
                        const tieneMultiplesPasajeros = obs.includes('--- Detalle de Ruta');
                        
                        if (tieneMultiplesPasajeros) {
                          // Parsear las observaciones para extraer los datos de pasajeros
                          const rutaMatch = obs.match(/--- Detalle de Ruta.*?---\n([\s\S]*?)(?=\n\n---|$)/);
                          const contactoMatch = obs.match(/--- Contactos ---\n([\s\S]*?)$/);
                          
                          if (!rutaMatch) return null;
                          
                          const rutaLines = rutaMatch[1].trim().split('\n').filter(l => l.trim());
                          const contactoLines = contactoMatch ? contactoMatch[1].trim().split('\n').filter(l => l.trim()) : [];
                          
                          return rutaLines.map((line, idx) => {
                            const rutaParts = line.match(/^\d+\.\s*(.+?):\s*(.+?)\s*→\s*(.+)$/);
                            const nombre = rutaParts ? rutaParts[1] : line;
                            const origen = rutaParts ? rutaParts[2] : '';
                            const destino = rutaParts ? rutaParts[3] : '';
                            
                            const contactoLine = contactoLines[idx] || '';
                            const telMatch = contactoLine.match(/Tel:\s*([^\|]+)/);
                            const emailMatch = contactoLine.match(/Email:\s*(.+)$/);
                            const tel = telMatch ? telMatch[1].trim() : '';
                            const email = emailMatch ? emailMatch[1].trim() : '';
                            
                            return (
                              <tr key={idx} className={idx === 0 ? 'bg-green-50' : ''}>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${idx === 0 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}>
                                    {idx + 1}
                                  </span>
                                </td>
                                <td className="px-3 py-2 font-medium">{nombre}</td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-1 text-xs">
                                    <MapPin size={12} className="text-green-600" />
                                    <span>{origen}</span>
                                    <ArrowRight size={12} className="text-gray-400 mx-1" />
                                    <MapPin size={12} className="text-red-600" />
                                    <span>{destino}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-600">
                                  {tel && <div>{tel}</div>}
                                  {email && <div className="text-gray-500">{email}</div>}
                                </td>
                              </tr>
                            );
                          });
                        } else {
                          // Pasajero individual - usar datos del servicio
                          const nombre = selectedServicio.nombre_pasajero || selectedServicio.pasajeros || 'Sin nombre';
                          return (
                            <tr className="bg-green-50">
                              <td className="px-3 py-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold bg-green-600 text-white">
                                  1
                                </span>
                              </td>
                              <td className="px-3 py-2 font-medium">{nombre}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1 text-xs">
                                  <MapPin size={12} className="text-green-600" />
                                  <span>{selectedServicio.origen}</span>
                                  <ArrowRight size={12} className="text-gray-400 mx-1" />
                                  <MapPin size={12} className="text-red-600" />
                                  <span>{selectedServicio.destino}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600">
                                {selectedServicio.telefono && <div>{selectedServicio.telefono}</div>}
                                {selectedServicio.correo && <div className="text-gray-500">{selectedServicio.correo}</div>}
                              </td>
                            </tr>
                          );
                        }
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {(selectedServicio.vehiculo_info || selectedServicio.conductor_nombre) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedServicio.vehiculo_info && (
                    <div>
                      <Label className="text-gray-600">Vehículo</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Car size={16} className="text-primary" />
                        <span className="font-medium">{selectedServicio.vehiculo_info}</span>
                      </div>
                    </div>
                  )}
                  {selectedServicio.conductor_nombre && (
                    <div>
                      <Label className="text-gray-600">Conductor</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <User size={16} className="text-primary" />
                        <span className="font-medium">{selectedServicio.conductor_nombre}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Bitácora del Conductor (visible para admin) */}
              {(selectedServicio.conductor_recepcion_fecha || selectedServicio.conductor_realizado_fecha || selectedServicio.conductor_suspendido_fecha || (selectedServicio.bitacoras_conductor && selectedServicio.bitacoras_conductor.length > 0)) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <Label className="text-gray-700 font-semibold mb-3 flex items-center gap-2">
                    <Truck size={16} className="text-primary" />
                    Bitácora del Conductor
                  </Label>
                  <div className="space-y-3 mt-2">
                    {selectedServicio.conductor_recepcion_fecha && (
                      <div className="flex gap-3" data-testid="admin-bitacora-recepcion">
                        <div className="w-1 bg-amber-400 rounded-full shrink-0"></div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">Servicio Recibido</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {new Date(selectedServicio.conductor_recepcion_fecha).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {selectedServicio.conductor_recepcion_texto && (
                            <p className="text-sm text-gray-700 mt-1 italic">"{selectedServicio.conductor_recepcion_texto}"</p>
                          )}
                        </div>
                      </div>
                    )}
                    {selectedServicio.conductor_realizado_fecha && (
                      <div className="flex gap-3" data-testid="admin-bitacora-realizado">
                        <div className="w-1 bg-green-500 rounded-full shrink-0"></div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">Servicio realizado por el conductor</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {new Date(selectedServicio.conductor_realizado_fecha).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {selectedServicio.conductor_realizado_nota && (
                            <p className="text-sm text-gray-700 mt-1 italic">"{selectedServicio.conductor_realizado_nota}"</p>
                          )}
                        </div>
                      </div>
                    )}
                    {selectedServicio.conductor_suspendido_fecha && (
                      <div className="flex gap-3" data-testid="admin-bitacora-suspendido">
                        <div className="w-1 bg-red-500 rounded-full shrink-0"></div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">Servicio suspendido por el conductor</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {new Date(selectedServicio.conductor_suspendido_fecha).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {selectedServicio.conductor_suspendido_nota && (
                            <p className="text-sm text-gray-700 mt-1 italic">"{selectedServicio.conductor_suspendido_nota}"</p>
                          )}
                        </div>
                      </div>
                    )}
                    {(selectedServicio.bitacoras_conductor || []).map((b, idx) => (
                      <div className="flex gap-3" key={`admin-bit-${idx}`} data-testid={`admin-bitacora-nota-${idx}`}>
                        <div className="w-1 bg-primary rounded-full shrink-0"></div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">Nota del conductor</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {new Date(b.fecha).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{b.texto}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tiempo de Espera */}
              {selectedServicio.estado === 'Realizado' && (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div>
                    <Label className="text-gray-600 text-sm">Tiempo de Espera</Label>
                    <p className="font-semibold text-lg" data-testid="tiempo-espera-value">
                      {selectedServicio.tiempo_espera > 0 
                        ? `${selectedServicio.tiempo_espera} min` 
                        : 'Sin tiempo de espera'}
                    </p>
                  </div>
                  {user?.role === 'admin' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEditTiempoEspera}
                      data-testid="edit-tiempo-espera-button"
                    >
                      <Pencil size={14} className="mr-1" />
                      Editar
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex items-center justify-center gap-2 mt-4 flex-wrap">
            {canDelete() && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setShowDetail(false);
                  setShowDelete(true);
                }}
                data-testid="delete-servicio-button"
              >
                Eliminar
              </Button>
            )}

            {canSuspend() && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleChangeEstado('Suspendido')}
                disabled={loading}
                data-testid="suspend-servicio-button"
              >
                Suspender
              </Button>
            )}

            {/* Mostrar mensaje para clientes cuando el servicio ya pasó */}
            {user?.role === 'client' && selectedServicio?.estado === 'Agendado' && isServicioPasado() && (
              <p className="w-full text-center text-sm text-gray-500 italic">
                Este servicio ya pasó y no puede ser modificado.
              </p>
            )}
            
            {user?.role === 'admin' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                  data-testid="edit-servicio-button"
                >
                  <Pencil size={14} className="mr-1" />
                  Editar
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAssign}
                  data-testid="assign-button"
                >
                  Asignar Recursos
                </Button>

                {selectedServicio?.conductor_id && selectedServicio?.estado !== 'Realizado' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenReasignar}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="reasignar-conductor-button"
                  >
                    <Repeat size={14} className="mr-1" />
                    Reasignar Conductor
                  </Button>
                )}
                
                {/* Botón Confirmar para admin - cuando está agendado y NO confirmado */}
                {canConfirmAdmin() && (
                  <Button
                    size="sm"
                    onClick={handleConfirmar}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-admin-button"
                  >
                    <CheckCircle size={14} className="mr-1" />
                    Confirmar
                  </Button>
                )}
                
                {/* Botón Marcar Realizado - cuando está agendado Y confirmado */}
                {canMarkRealizado() && (
                  <Button
                    size="sm"
                    onClick={() => handleChangeEstado('Realizado')}
                    disabled={loading}
                    data-testid="mark-realizado-button"
                  >
                    Marcar Realizado
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal para ingresar tarifa al confirmar */}
      <Dialog open={showTarifaModal} onOpenChange={setShowTarifaModal}>
        <DialogContent className="max-w-md" data-testid="tarifa-modal">
          <DialogHeader>
            <DialogTitle>Ingresar Tarifa para Confirmar</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Para confirmar este servicio, debe ingresar el valor de la tarifa.
            </p>
            
            <div>
              <Label htmlFor="tarifa-confirmar">Tarifa (CLP) *</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary" size={20} />
                <Input
                  id="tarifa-confirmar"
                  type="number"
                  value={tarifaConfirmar}
                  onChange={(e) => setTarifaConfirmar(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="pl-10 text-lg font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="Ingrese la tarifa"
                  autoFocus
                  data-testid="tarifa-confirmar-input"
                />
              </div>
            </div>

            {tarifaConfirmar && parseFloat(tarifaConfirmar) > 0 && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total:</span>
                  <span className="text-xl font-bold text-primary">
                    ${parseFloat(tarifaConfirmar).toLocaleString('es-CL')}
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowTarifaModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmarConTarifa}
              disabled={loading || !tarifaConfirmar || parseFloat(tarifaConfirmar) <= 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-with-tarifa-btn"
            >
              <CheckCircle size={16} className="mr-2" />
              {loading ? 'Confirmando...' : 'Confirmar Servicio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reasignar Conductor por contingencia */}
      <Dialog open={showReasignar} onOpenChange={(v) => { if (!reasignarLoading) setShowReasignar(v); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md" data-testid="reasignar-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat size={18} className="text-amber-700" />
              Reasignar Conductor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedServicio?.conductor_nombre && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm">
                <p className="text-xs text-gray-500">Conductor actual</p>
                <p className="font-medium text-gray-900">{selectedServicio.conductor_nombre}</p>
                <p className="text-xs text-gray-500 mt-2">Estado actual</p>
                <p className="font-medium text-gray-900">{selectedServicio.estado}</p>
              </div>
            )}
            <div>
              <Label>Nuevo Conductor *</Label>
              <Select value={reasignarConductor} onValueChange={setReasignarConductor}>
                <SelectTrigger data-testid="reasignar-conductor-select">
                  <SelectValue placeholder="Seleccionar conductor" />
                </SelectTrigger>
                <SelectContent>
                  {conductores
                    .filter(c => c.id !== selectedServicio?.conductor_id)
                    .map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nombre} {c.rut ? `· ${c.rut}` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motivo de la reasignación (opcional)</Label>
              <Input
                value={reasignarMotivo}
                onChange={(e) => setReasignarMotivo(e.target.value)}
                placeholder="Ej: Conductor original con avería mecánica"
                data-testid="reasignar-motivo-input"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
              ⚠️ Al reasignar, el estado del servicio vuelve a <strong>Agendado</strong> y se borra la bitácora del conductor anterior (mensaje de recepción/realizado/suspendido). Queda registro en Observaciones con fecha, conductor anterior y motivo.
            </div>
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setShowReasignar(false)} className="w-full sm:w-auto" disabled={reasignarLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleReasignarSubmit}
              disabled={reasignarLoading || !reasignarConductor}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
              data-testid="reasignar-submit"
            >
              {reasignarLoading ? 'Reasignando...' : 'Reasignar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asignar Vehículo/Conductor */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent data-testid="assign-modal">
          <DialogHeader>
            <DialogTitle>Asignar Recursos</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Vehículo</Label>
              <Select value={selectedVehiculo} onValueChange={setSelectedVehiculo}>
                <SelectTrigger data-testid="vehiculo-select">
                  <SelectValue placeholder="Seleccionar vehículo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="null">Sin asignar</SelectItem>
                  {vehiculos.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.marca} {v.modelo} - {v.patente}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Conductor</Label>
              <Select value={selectedConductor} onValueChange={setSelectedConductor}>
                <SelectTrigger data-testid="conductor-select">
                  <SelectValue placeholder="Seleccionar conductor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="null">Sin asignar</SelectItem>
                  {conductores.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nombre} - {c.rut}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveAssignment} disabled={loading} data-testid="save-assignment-button">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar Eliminación */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent data-testid="delete-confirmation-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedServicio && (
                <div className="space-y-2 text-left mt-4">
                  <p className="font-semibold text-gray-900">Estás a punto de eliminar el siguiente servicio:</p>
                  <div className="bg-gray-50 p-3 rounded-md space-y-1 text-sm">
                    <p><span className="font-medium">Fecha:</span> {formatDate(selectedServicio.fecha)} - {selectedServicio.hora}</p>
                    <p><span className="font-medium">Ruta:</span> {resumirRutaServicio(selectedServicio).origen} → {resumirRutaServicio(selectedServicio).destino}</p>
                  </div>
                  <p className="text-red-600 font-medium mt-3">⚠️ Esta acción es irreversible</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
              data-testid="confirm-delete-button"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Confirmar Realizado con opción de editar tarifa */}
      <Dialog open={showRealizadoModal} onOpenChange={setShowRealizadoModal}>
        <DialogContent className="max-w-md" data-testid="realizado-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Manrope'] flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              Marcar como Realizado
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-gray-600">
              Antes de cerrar este servicio, puede ajustar la tarifa final si es necesario.
            </p>

            {selectedServicio && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Folio:</span>
                  <span className="font-medium">#{selectedServicio.folio}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Fecha:</span>
                  <span className="font-medium">{selectedServicio.fecha}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Ruta:</span>
                  <span className="font-medium">{resumirRutaServicio(selectedServicio).origen} → {resumirRutaServicio(selectedServicio).destino}</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="tarifa-realizado" className="text-base font-medium">
                Tarifa Final
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  id="tarifa-realizado"
                  type="number"
                  min="0"
                  step="100"
                  value={tarifaRealizado}
                  onChange={(e) => setTarifaRealizado(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="pl-10 text-lg font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="0"
                  data-testid="tarifa-realizado-input"
                />
              </div>
              <p className="text-xs text-gray-500">
                Tarifa actual: ${selectedServicio?.tarifa?.toLocaleString('es-CL') || '0'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tiempo-espera" className="text-base font-medium">
                Tiempo de Espera (minutos)
              </Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  id="tiempo-espera"
                  type="number"
                  min="0"
                  step="5"
                  value={tiempoEspera}
                  onChange={(e) => setTiempoEspera(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="pl-10 text-lg font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="0"
                  data-testid="tiempo-espera-input"
                />
              </div>
              <p className="text-xs text-gray-500">
                Ingrese 0 si no hubo tiempo de espera
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowRealizadoModal(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmarRealizado}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-realizado-button"
            >
              {loading ? 'Procesando...' : 'Confirmar y Cerrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Tiempo de Espera */}
      <Dialog open={showTiempoEsperaModal} onOpenChange={setShowTiempoEsperaModal}>
        <DialogContent className="max-w-sm" data-testid="tiempo-espera-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Manrope'] flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Editar Tiempo de Espera
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedServicio && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <span className="text-gray-500">Folio:</span>{' '}
                <span className="font-medium">#{selectedServicio.folio}</span>
                <span className="mx-2 text-gray-300">|</span>
                <span className="text-gray-500">Fecha:</span>{' '}
                <span className="font-medium">{selectedServicio.fecha}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="tiempo-espera-edit" className="text-base font-medium">
                Tiempo de Espera (minutos)
              </Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  id="tiempo-espera-edit"
                  type="number"
                  min="0"
                  step="5"
                  value={tiempoEsperaEdit}
                  onChange={(e) => setTiempoEsperaEdit(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="pl-10 text-lg font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="0"
                  data-testid="tiempo-espera-edit-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowTiempoEsperaModal(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleGuardarTiempoEspera}
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-tiempo-espera-button"
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Servicio */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-servicio-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold font-['Manrope']">Editar Servicio</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-fecha">Fecha *</Label>
                <Input
                  id="edit-fecha"
                  type="date"
                  value={editForm.fecha}
                  onChange={(e) => setEditForm({ ...editForm, fecha: e.target.value })}
                  data-testid="edit-fecha-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-hora">Hora *</Label>
                <Input
                  id="edit-hora"
                  type="time"
                  value={editForm.hora}
                  onChange={(e) => setEditForm({ ...editForm, hora: e.target.value })}
                  data-testid="edit-hora-input"
                />
              </div>
            </div>

            <div className="space-y-3 border-t border-b border-gray-200 py-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">
                  Pasajeros ({editPasajeros.length})
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={agregarPasajeroEdit}
                  data-testid="edit-agregar-pasajero"
                >
                  <Plus size={14} className="mr-1" />
                  Agregar Pasajero
                </Button>
              </div>
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                {editPasajeros.map((p, idx) => (
                  <PasajeroItem
                    key={idx}
                    pasajero={p}
                    index={idx}
                    total={editPasajeros.length}
                    onUpdate={actualizarPasajeroEdit}
                    onRemove={eliminarPasajeroEdit}
                    onMoveUp={moverPasajeroEditArriba}
                    onMoveDown={moverPasajeroEditAbajo}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-tarifa" className="text-primary font-semibold">Tarifa (CLP) *</Label>
                <Input
                  id="edit-tarifa"
                  type="number"
                  min="0"
                  value={editForm.tarifa}
                  onChange={(e) => setEditForm({ ...editForm, tarifa: e.target.value })}
                  onWheel={(e) => e.target.blur()}
                  className="text-lg font-bold border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="Ingrese la tarifa"
                  data-testid="edit-tarifa-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-centro-costo">Centro de Costo</Label>
                <Select
                  value={editForm.centro_costo || "ninguno"}
                  onValueChange={(value) => setEditForm({ ...editForm, centro_costo: value === "ninguno" ? "" : value })}
                >
                  <SelectTrigger id="edit-centro-costo" data-testid="edit-centro-costo-select">
                    <SelectValue placeholder="Seleccionar centro de costo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ninguno">Ninguno</SelectItem>
                    {centrosCostoEdit.map(cc => (
                      <SelectItem key={cc} value={cc}>{cc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-tipo-viaje">Tipo de Viaje</Label>
              <Select
                value={editForm.tipo_viaje || "ninguno"}
                onValueChange={(value) => setEditForm({ ...editForm, tipo_viaje: value === "ninguno" ? "" : value })}
              >
                <SelectTrigger id="edit-tipo-viaje" data-testid="edit-tipo-viaje-select">
                  <SelectValue placeholder="Seleccionar tipo de viaje" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ninguno">Sin especificar</SelectItem>
                  {tiposViaje.map(t => (
                    <SelectItem key={t.id} value={t.nombre}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="edit-asignacion-recursos">
              <div>
                <Label htmlFor="edit-conductor">Conductor</Label>
                <Select
                  value={editForm.conductor_id || '__none__'}
                  onValueChange={(v) => setEditForm({ ...editForm, conductor_id: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger id="edit-conductor" data-testid="edit-conductor-select">
                    <SelectValue placeholder="Sin asignar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin asignar</SelectItem>
                    {conductores.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}{c.rut ? ` · ${c.rut}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-vehiculo">Vehículo</Label>
                <Select
                  value={editForm.vehiculo_id || '__none__'}
                  onValueChange={(v) => setEditForm({ ...editForm, vehiculo_id: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger id="edit-vehiculo" data-testid="edit-vehiculo-select">
                    <SelectValue placeholder="Sin asignar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin asignar</SelectItem>
                    {vehiculos.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.patente || v.placa} · {v.modelo || v.marca}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-observaciones">Observaciones</Label>
              <Input
                id="edit-observaciones"
                value={editForm.observaciones}
                onChange={(e) => setEditForm({ ...editForm, observaciones: e.target.value })}
                placeholder="Observaciones adicionales"
                data-testid="edit-observaciones-input"
              />
            </div>

            {/* Ruta del Vehículo: desvíos y retorno */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3" data-testid="edit-ruta-section">
              <div className="flex items-center gap-2">
                <Route size={18} className="text-primary" />
                <h3 className="font-semibold text-gray-900">Ruta del Vehículo</h3>
              </div>
              <p className="text-xs text-gray-500 -mt-2">Paradas intermedias y/o ida y vuelta del vehículo.</p>

              {editDesvios.length > 0 && (
                <div className="space-y-2">
                  {editDesvios.map((d, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`edit-desvio-row-${idx}`}>
                      <span className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <Input
                        value={d}
                        onChange={(e) => {
                          const next = [...editDesvios];
                          next[idx] = e.target.value;
                          setEditDesvios(next);
                        }}
                        placeholder={`Desvío ${idx + 1}`}
                        className="bg-white"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditDesvios(editDesvios.filter((_, i) => i !== idx))}
                        title="Quitar desvío"
                      >
                        <X size={16} className="text-red-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setEditDesvios([...editDesvios, ''])} data-testid="edit-add-desvio">
                  <Plus size={14} className="mr-1" /> Agregar Desvío
                </Button>
                <Button
                  type="button"
                  variant={editTieneRetorno ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setEditTieneRetorno(!editTieneRetorno)}
                  data-testid="edit-toggle-retorno"
                >
                  <Repeat size={14} className="mr-1" />
                  {editTieneRetorno ? 'Quitar Retorno' : 'Agregar Retorno'}
                </Button>
              </div>

              {editTieneRetorno && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                  🔄 El vehículo regresará al origen tras dejar a los pasajeros.
                </p>
              )}
            </div>

            {editForm.tarifa && parseFloat(editForm.tarifa) > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg border">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total del servicio:</span>
                  <span className="text-2xl font-bold text-primary">
                    ${parseFloat(editForm.tarifa).toLocaleString('es-CL')}
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEdit(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={loading} data-testid="save-edit-button">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
